/**
 * MachinesTab — list all machines in the admin's org scope.
 * ATOMS admins can commission new machines; OEM admins can edit machines in scope.
 * Clicking a machine navigates to the full-page MachineEditPage wizard.
 */
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Badge, ActionIcon, Group, Text, TextInput,
  Loader, Alert, Tooltip, Stack,
} from '@mantine/core';
import {
  MdEdit, MdAddCircleOutline, MdSearch, MdRefresh,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { getMachines } from '../../API/adminAPI';

const MachinesTab = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const role = localStorage.getItem('role') || 'user';
  const canCreate = role === 'atoms_admin';

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getMachines();
      setMachines(data.machines || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load machines');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = machines.filter(
    (m) =>
      m.machine_id?.toLowerCase().includes(search.toLowerCase()) ||
      m.machine_name?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => navigate('/app/machines/new');
  const openEdit = (m) => navigate(`/app/machines/${m.id}/edit`, { state: { machine: m } });

  if (loading) return <Loader mt="xl" mx="auto" />;

  return (
    <Stack>
      {error && <Alert color="red">{error}</Alert>}

      <Group position="apart">
        <TextInput
          placeholder="Search machines…"
          icon={<MdSearch />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          sx={{ flex: 1, maxWidth: 320 }}
        />
        <Group spacing="xs">
          <ActionIcon onClick={load} variant="subtle" title="Refresh">
            <MdRefresh size={20} />
          </ActionIcon>
          {canCreate && (
            <Button leftIcon={<MdAddCircleOutline />} onClick={openCreate}>
              Commission Machine
            </Button>
          )}
        </Group>
      </Group>

      <Table striped highlightOnHover>
        <thead>
          <tr>
            <th>Machine ID</th>
            <th>Name</th>
            <th>Owned By</th>
            <th>Channels</th>
            <th style={{ width: 60 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={5}>
                <Text color="dimmed" align="center" py="md">
                  No machines found
                </Text>
              </td>
            </tr>
          ) : (
            filtered.map((m) => (
              <tr
                key={m.id}
                style={{ cursor: 'pointer' }}
                onClick={() => openEdit(m)}
              >
                <td>
                  <Badge variant="outline" color="blue" size="sm">
                    {m.machine_id}
                  </Badge>
                </td>
                <td>
                  <Text size="sm" weight={500}>
                    {m.machine_name}
                  </Text>
                </td>
                <td>
                  <Text size="sm" color="dimmed">
                    {m.owned_by || '—'}
                  </Text>
                </td>
                <td>
                  <Badge size="sm" color="gray" variant="light">
                    {m.channel_count ?? '—'} channels
                  </Badge>
                </td>
                <td>
                  <Tooltip label="Edit machine">
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(m);
                      }}
                    >
                      <MdEdit />
                    </ActionIcon>
                  </Tooltip>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Stack>
  );
};

export default MachinesTab;
